import { useQueryClient } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { InlineProposalPanel } from '@/components/proposals/InlineProposalPanel';
import { BuilderChatHeader } from '@/components/reasoning/BuilderChatHeader';
import { BuilderRecordPanelDesktop } from '@/components/reasoning/BuilderRecordPanel';
import { BuilderRecordPanelMobileOverlay } from '@/components/reasoning/BuilderRecordPanel';
import { BuilderReviewBar } from '@/components/reasoning/BuilderReviewBar';
import { ConversationModals } from '@/components/reasoning/ConversationModals';
import type { ReasoningStepData } from '@/components/reasoning/ReasoningStepsList';
import { useSlideoutWidth } from '@/contexts/SlideoutWidthContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  INLINE_PANEL_MAX_WIDTH,
  INLINE_PANEL_MIN_WIDTH,
  useCanHostInlinePanel,
} from '@/hooks/useCanHostInlinePanel';
import { useUserProfileData } from '@/hooks/useUserProfileData';
import { EdgeResizeHandle } from '@/lib/base/resize-handle/edge-resize-handle';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import {
  ENTITY_BUILDER_AGENT_TYPE,
  reconcileBuilderRoute,
  useBuilderMode,
} from '@/lib/crm/hooks/useBuilderMode';
import { useBuilderSubject } from '@/lib/crm/hooks/useBuilderSubject';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import { fetchWithDefaults } from '@/lib/http/client';
import { proposalQueryKeys } from '@/lib/proposals/query/keys';
import { getBuilderPlaceholder } from '@/lib/reasoning/builderPlaceholder';
import { conversationService } from '@/lib/reasoning/conversation';
import { useChainFeedbackModal } from '@/lib/reasoning/hooks/useChainFeedbackModal';
import { useChatAutoScroll } from '@/lib/reasoning/hooks/useChatAutoScroll';
import { useProposalPaneResize } from '@/lib/reasoning/hooks/useProposalPaneResize';
import { useReasoningSidebarPanel } from '@/lib/reasoning/hooks/useReasoningSidebarPanel';
import type { ChatProgressData } from '@/lib/reasoning/realtime/chatProgressPoll';
import { classifyChatProgress } from '@/lib/reasoning/realtime/chatProgressPoll';
import {
  setConversationHidden,
  setConversationVisible,
  trackChatRun,
} from '@/lib/reasoning/realtime/chatRunTracker';
import {
  mergeStepProgress,
  reconcilePolledSteps,
} from '@/lib/reasoning/realtime/partialSteps';
import { resolveTaskId } from '@/lib/reasoning/realtime/resolveTaskId';
import { resumeActiveRun } from '@/lib/reasoning/realtime/resumeActiveRun';
import type {
  ChainCompletedEvent,
  ChainFailedEvent,
  StepProgressEvent,
  TitleUpdatedEvent,
} from '@/lib/reasoning/realtime/types';
import { useChatProgress } from '@/lib/reasoning/realtime/useChatProgress';
import { useViewHealthMarker } from '@/lib/view-health';

import type { UploadedFile } from '../../components/files';
import { MaterialsList } from '../../components/reasoning/MaterialsList';
import { useProposalSidebar } from '../../contexts/ActionSidebarContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useTitle } from '../../hooks/useTitle';
import { resolveChatColumnWidth } from './chat/chatColumnWidth';
import { ChatConversationThread } from './chat/ChatConversationThread';
import { ChatMainColumn } from './chat/ChatMainColumn';
import { ChatSidePanel } from './chat/ChatSidePanel';
import type { ChatTab } from './chat/ChatThreadHeader';
import { ChatThreadHeader } from './chat/ChatThreadHeader';
import { FollowUpComposer } from './chat/FollowUpComposer';
import { NewMessagesButton } from './chat/NewMessagesButton';
import { TeamRequiredState } from './chat/TeamRequiredState';
import type {
  ChatChain as Chain,
  ChatStep as ReasoningStep,
} from './chat/types';
import { useAutoOpenLatestProposal } from './hooks/useAutoOpenLatestProposal';
import { TableScrollProvider } from './TableScrollContext';

interface Conversation {
  id: string;
  title: string;
  initial_prompt: string;
  state: string;
  created_by: number;
  /**
   * What kind of conversation this is. `entity_builder` marks a builder
   * conversation — the authority the route reconciles against, so the mode
   * survives arriving from the conversation list, a bookmark or a shared link.
   */
  agent_type?: string;
  /** The entity a builder conversation is building. */
  builder_entity_type?: string | null;
  /** Null until the agent's first proposal mints the id. */
  builder_entity_id?: string | null;
}

interface ConversationListItem {
  id: string;
  title: string;
  initial_prompt: string;
  state: string;
  created_at: string;
}

// loadConversation retry policy: transient failures (network errors, 5xx,
// 429) get one automatic retry before surfacing anything to the user. Other
// 4xx responses are permanent — retrying cannot change the outcome.
const LOAD_CONVERSATION_MAX_ATTEMPTS = 2;
const LOAD_CONVERSATION_RETRY_DELAY_MS = 1000;

/** Follow-up composer placeholder outside builder mode. */
const DEFAULT_FOLLOW_UP_PLACEHOLDER = 'What can I help with today?';

// Docked-proposal eligibility (`useCanHostInlinePanel`, shared with
// ActionDetailPage): the chat column must keep at least this much width
// beside the nav sidebar and the pane, or the claim is dropped and the
// global overlay renders instead.
const MIN_CHAT_WIDTH_FOR_DOCKED_PROPOSAL = 560;

const wait = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * The most recent proposal in a conversation, by the chronology builder mode
 * uses (chains and steps are ordered; the last listed occurrence is the most
 * recent). `pendingOnly` narrows the scan to proposals that are still OPEN —
 * the two consumers below deliberately differ on this (see their comments).
 */
function lastCreatedProposalId(
  chains: Chain[],
  pendingOnly: boolean
): number | undefined {
  let last: number | undefined;
  for (const chain of chains) {
    for (const step of chain.steps || []) {
      for (const proposal of step.created_proposals || []) {
        if (!pendingOnly || proposal.resolution === 'pending') {
          last = proposal.id;
        }
      }
    }
  }
  return last;
}

export default function ReasoningChat() {
  useTitle('Conversations');

  const { orgId, conversationId } = useParams<{
    orgId?: string;
    conversationId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Add ref for scrollable container
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const [, setConversations] = useState<ConversationListItem[]>([]);
  const [currentConversation, setCurrentConversation] =
    useState<Conversation | null>(null);
  const [chains, setChains] = useState<Chain[]>([]);
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [optimisticMessage, setOptimisticMessage] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setCollapsedChains] = useState<Set<string>>(new Set());
  const [, setCollapsedSteps] = useState<Set<string>>(new Set());
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [pollingConversationId, setPollingConversationId] = useState<
    string | null
  >(null);
  const [progressStatus, setProgressStatus] = useState<string>('');
  const [partialSteps, setPartialSteps] = useState<ReasoningStepData[]>([]);
  // Dedicated polling-failure flag used by the `polling-ready` view-health
  // marker. Gating on the global `error` state would be too broad — unrelated
  // failures (e.g. a failed regenerate/follow-up request) would suppress the
  // marker during the initial load window and produce false timeout beacons.
  const [pollingFailed, setPollingFailed] = useState(false);

  // Reset the follow-up composer draft (text and attachments) when the user
  // switches to a different conversation. Both the /conversations/:id and
  // /build/:id routes render this same element with no key tied to
  // conversationId, so the component stays mounted across a switch and only the
  // route param changes. Compare the id in render (React's sanctioned
  // prop-change reset) rather than a prop-to-state sync effect. Staying in the
  // same conversation (re-render, poll, proposal-panel change) leaves
  // conversationId unchanged, so the draft is kept; a send failure never changes
  // conversationId, so the send-failure rollback below is preserved.
  const [draftConversationId, setDraftConversationId] =
    useState(conversationId);
  if (conversationId !== draftConversationId) {
    setDraftConversationId(conversationId);
    setFollowUpMessage('');
    setUploadedFiles([]);
  }

  useViewHealthMarker('conversation-data', {
    ready:
      currentConversation !== null &&
      String(currentConversation.id) === String(conversationId),
  });

  // Register this conversation as visible while mounted so the chat run
  // tracker suppresses its own completion toast (the user is already looking
  // at the conversation). Guarded because the no-conversation home routes
  // also render ReasoningChat with an undefined conversationId.
  useLayoutEffect(() => {
    if (!conversationId) {
      return;
    }
    setConversationVisible(conversationId);
    return () => {
      setConversationHidden(conversationId);
    };
  }, [conversationId]);

  // Refs for stable callbacks (avoid stale closures in useCallback)
  const currentTaskIdRef = useRef<string | null>(null);
  const processedTaskIdRef = useRef<string | null>(null);
  // Conversation id currently rendered on screen. Lets loadConversation
  // distinguish an initial load or post-navigation reload (nothing visible —
  // a failure must show the blocking banner) from a background refresh of
  // content the user is already reading (a failure should not blank that
  // out with a banner). Mirrors `currentConversation` so the check is tied
  // to what the user actually sees, not just what was last fetched.
  const renderedConversationIdRef = useRef<string | null>(null);
  // The conversationId the previous render was showing. Lets Effect 1 detect a
  // genuine switch to a different conversation (vs. a follow-up in the same one,
  // which never changes conversationId) so it can drop the outgoing
  // conversation's task before adopting the destination's.
  const previousConversationIdRef = useRef<string | null>(null);
  // Monotonic counter identifying the most recent loadConversation invocation.
  // A rapid A → B → A switch fires three loads; only the latest may install its
  // result. Each call captures its generation at entry and the success path
  // bails once a newer load has superseded it. The conversation-id route check
  // alone cannot catch A → B → A: the stale A response's id matches the route
  // again, so it would pass an id-only gate and overwrite the newer A load's
  // chains/task state. (The failure path guards on the route, not the
  // generation — see the note there — so overlapping same-conversation refresh
  // failures still surface their toast.)
  const loadGenerationRef = useRef(0);
  // Generation of the most recent loadConversation that SUCCEEDED (rendered
  // content). Lets the failure path suppress a stale failure only when a
  // strictly-newer load has already succeeded — see loadConversation.
  const lastSucceededLoadGenerationRef = useRef(0);

  useEffect(() => {
    renderedConversationIdRef.current = currentConversation
      ? String(currentConversation.id)
      : null;
  }, [currentConversation]);

  // Keep currentTaskIdRef in sync with state
  useEffect(() => {
    currentTaskIdRef.current = currentTaskId;
  }, [currentTaskId]);

  // User profile data
  const { data: profileData } = useUserProfileData();
  const userProfile = profileData?.user;

  // Materials state (combines proposals and dynamic views)
  const [activeTab, setActiveTab] = useState<ChatTab>('conversation');

  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const handleCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);
  const handleOpenShareModal = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  // Chat header handlers
  const handleNavigateBack = useCallback(() => {
    navigate(`/${orgId}/conversations`);
  }, [navigate, orgId]);
  const handleToggleMaterialsTab = useCallback(() => {
    setActiveTab(prev => (prev === 'materials' ? 'conversation' : 'materials'));
  }, []);

  // Regenerating chain state (tracks which chain is being regenerated)
  const [regeneratingChainId, setRegeneratingChainId] = useState<string | null>(
    null
  );

  // The panel's percent-width/push-animation state (isAnimating, sidebarWidth,
  // handleResizeStart, resizeSidebarBy) is no longer consumed: the panel is a
  // DockedSidePanel sized by the shared slideout width, resized with the same
  // px handlers as the docked proposal pane. Deleting that state from the hook
  // is tracked follow-up cleanup.
  const {
    sidebarDynamicViewId,
    sidebarChainId,
    handleOpenDynamicViewSidebar,
    handleOpenReasoningSidebar,
    handleCloseSidebar,
    closeReasoningSidebar,
  } = useReasoningSidebarPanel();

  const {
    chainFeedback,
    feedbackModalOpen,
    feedbackModalChainId,
    feedbackModalSteps,
    handleFeedback,
    handleStructuredFeedback,
    handleFeedbackModalClose,
  } = useChainFeedbackModal({ orgId, conversationId, chains });

  // Proposal sidebar hook for opening proposals
  const {
    openProposalId,
    sidebarWidth: proposalSidebarWidth,
    isResizing: proposalIsResizing,
    isSidebarVisible: proposalSidebarVisible,
    proposalUpdateCounter,
    openProposal,
  } = useProposalSidebar();
  const queryClient = useQueryClient();

  // Builder mode: the object being built sits beside the chat rather than over
  // it, so the chat column gives up width. The panel is a real child of the
  // layout (see BuilderSplit) — not the floating entity slideout it used to
  // borrow — so "push" is purely this column shrinking against a real sibling.
  const { isBuilderMode } = useBuilderMode();
  const {
    width: slideoutWidth,
    setWidth: setSlideoutWidth,
    isResizing: slideoutIsResizing,
    setIsResizing: setSlideoutIsResizing,
  } = useSlideoutWidth();

  // The conversation is the authority on what it is; the route is a rendering
  // hint that must follow it. Reconciling here is what makes a builder
  // conversation impossible to open "incorrectly" — arriving from the
  // conversation list, a bookmark or a shared link lands on the right route
  // instead of silently rendering a chat with no panel.
  const builderOrgId =
    orgId !== undefined ? parseInt(orgId, 10) || undefined : undefined;
  React.useEffect(() => {
    if (builderOrgId === undefined || conversationId === undefined) {
      return;
    }
    const target = reconcileBuilderRoute({
      orgId: builderOrgId,
      conversationId,
      agentType: currentConversation?.agent_type,
      isBuilderRoute: isBuilderMode,
      // Load-bearing. Without it the redirect below lands on a bare pathname
      // and, being `replace: true`, erases every query param with no history
      // entry to recover them from — closing an open `?slideoutEntityId=`
      // record. Same hazard the nav-state effect further down already guards.
      search: location.search,
    });
    if (target) {
      navigate(target, { replace: true });
    }
  }, [
    builderOrgId,
    conversationId,
    currentConversation?.agent_type,
    isBuilderMode,
    location.search,
    navigate,
  ]);

  // What the view renders is decided by the conversation, not the URL. Before
  // the conversation loads the route is the only hint available — and it is
  // the route we navigated from — so it stands in for that window only. Once
  // `agent_type` arrives it wins outright, which is what makes opening a
  // builder conversation from the conversation list, a bookmark or a shared
  // link land in builder mode rather than a chat with a missing panel.
  const isBuilderConversationView =
    conversationId === undefined ||
    currentConversation?.agent_type === undefined
      ? isBuilderMode
      : currentConversation.agent_type === ENTITY_BUILDER_AGENT_TYPE;

  // Fail closed until a live fetch resolves: the persisted-cache fast path in
  // `useFeatureFlags` would otherwise let a stale/errored value flash the inline
  // variant for non-builder chats, violating the default-off/no-flash contract.
  const isInlineProposalsEnabled = useFeatureFlag('inline_proposals', {
    failClosedUntilFresh: true,
  });
  const showInline = isInlineProposalsEnabled || isBuilderConversationView;

  // Proposals created anywhere in this conversation, oldest first. Under the
  // V1 guidance-only invariant the agent is asked to revise the existing
  // proposal rather than issue a new one, but nothing enforces it — so take
  // the newest and treat earlier ones as superseded.
  //
  // Deduplicated, LAST occurrence wins: `update_proposed_action` re-lists an
  // existing proposal on the step that edited it, so a proposal touched after
  // a newer one was created is the more recently worked-on draft. Keeping one
  // entry per id also makes the hook's "is the selection still a candidate"
  // membership check exact.
  const builderProposalIds = React.useMemo(() => {
    if (!isBuilderConversationView) {
      return [];
    }
    const ordered = chains.flatMap(chain =>
      (chain.steps || []).flatMap(step =>
        (step.created_proposals || []).map(proposal => proposal.id)
      )
    );
    const lastIndexById = new Map<number, number>();
    ordered.forEach((id, index) => lastIndexById.set(id, index));
    return ordered.filter((id, index) => lastIndexById.get(id) === index);
  }, [isBuilderConversationView, chains]);

  // Ordinary chats: the most recently created/touched proposal, by the same
  // chronology builder mode uses (chains and steps are ordered; the last
  // listed occurrence is the most recent — see builderProposalIds above).
  // Drives the docked pane's auto-open.
  const latestProposalId = React.useMemo(
    () =>
      isBuilderConversationView
        ? undefined
        : lastCreatedProposalId(chains, false),
    [isBuilderConversationView, chains]
  );

  // The most recent proposal that is still OPEN (pending) — the fallback for
  // which proposal a message is "about" when none is explicitly open in the
  // panel. Deliberately a separate rule from latestProposalId above: auto-open
  // shows the newest proposal regardless of resolution (builder semantics),
  // while "the proposal" in a user's message can only mean one that is still
  // actionable. The client's resolution copy can be momentarily stale after
  // an approve; that is fine — the backend re-fetches at grounding time and
  // states the true resolution, so a stale pick self-heals server-side.
  const latestOpenProposalId = React.useMemo(
    () =>
      isBuilderConversationView
        ? undefined
        : lastCreatedProposalId(chains, true),
    [isBuilderConversationView, chains]
  );

  // The user's override of which proposal drives the builder panel — set by
  // clicking an inline proposal card when the conversation has more than one.
  // `undefined` means "no override," which is what keeps the create flow
  // unchanged: the default stays "most recent," never "most recent pending."
  const [selectedProposalId, setSelectedProposalIdState] = useState<
    number | undefined
  >(undefined);
  const handleSelectProposal = useCallback((proposalId: number) => {
    setSelectedProposalIdState(proposalId);
  }, []);

  const {
    entityId: builderSubjectId,
    entityTypeName: builderSubjectEntityTypeName,
    draft: builderDraft,
    overlays: builderOverlays,
    mostRecentProposalId: mostRecentBuilderProposalId,
  } = useBuilderSubject({
    orgId: builderOrgId,
    proposalIds: builderProposalIds,
    // The conversation records its own subject, so the panel no longer
    // depends on a URL param surviving navigation.
    pinnedEntityId: currentConversation?.builder_entity_id ?? undefined,
    // Always set on a builder conversation, and unlike the draft it survives
    // approval — without it the panel loses the entity type the moment the
    // build is saved.
    pinnedEntityTypeName: currentConversation?.builder_entity_type ?? undefined,
    selectedProposalId,
  });

  // Builder conversations get a placeholder describing what can be built here;
  // ordinary chat keeps the generic one. `hasSubject` counts the draft as well
  // as the saved record — the moment the agent proposes something, the panel
  // has content and the next message refines it rather than starts it.
  const followUpPlaceholder = isBuilderConversationView
    ? getBuilderPlaceholder({
        entityTypeName: builderSubjectEntityTypeName,
        hasSubject: Boolean(builderSubjectId || builderDraft),
      })
    : DEFAULT_FOLLOW_UP_PLACEHOLDER;

  // A new proposal is the agent answering the user — pinning the panel to a
  // selection made before that answer arrived would hide it. Clearing here
  // (rather than never clearing) is what keeps a stale selection from
  // silently outliving the turn it was made in.
  const prevMostRecentBuilderProposalIdRef = useRef(
    mostRecentBuilderProposalId
  );
  useEffect(() => {
    if (
      mostRecentBuilderProposalId !== prevMostRecentBuilderProposalIdRef.current
    ) {
      prevMostRecentBuilderProposalIdRef.current = mostRecentBuilderProposalId;
      setSelectedProposalIdState(undefined);
    }
  }, [mostRecentBuilderProposalId]);

  // Reserve the strip for the whole session, not just once there is something
  // to put in it. The build route already shows the panel (with its own empty
  // state) before the first message, so gating on the subject here would make
  // the panel vanish the instant the user sends and reappear when the agent's
  // proposal lands — the layout jumping twice around a single action. The
  // panel renders its own empty state in the gap.
  // Gated on the SAME breakpoint the panel's own `hidden lg:flex` uses. The
  // panel's visibility is CSS-driven and this width reservation is JS-driven;
  // when the two disagree below `lg` the panel vanishes while the chat column
  // still gives up ~600px, squeezing the thread to nothing beside an empty
  // gap. Keep these two in lockstep.
  const isLargeScreen = useBreakpoint('lg');
  const builderPanelVisible = isBuilderConversationView && isLargeScreen;
  // Below `lg` in builder mode: the record is the primary surface; the chat
  // thread + composer are docked at the bottom. Mutually exclusive with
  // `builderPanelVisible` (one is !isLargeScreen, the other is isLargeScreen),
  // so only ONE BuilderPanel is ever in the DOM at a time.
  const isMobileBuilderView = isBuilderConversationView && !isLargeScreen;

  // ── Docked proposal pane ─────────────────────────────────────────────────
  // Proposals open SIDE BY SIDE with the chat (like the builder record
  // panel), not as the global slideout overlay. This page claims the
  // `proposal` kind via registerInlineHost while the viewport has room; the
  // global SlideoutRenderer stands down and the pane renders as a flex
  // sibling below. Dropping the claim (narrow viewport, builder mode) hands
  // rendering back to the global overlay automatically — the same seam
  // ActionDetailPage uses for `entity`. Optional context, like that page:
  // without a SlideoutStackProvider there is simply no docked pane.
  const slideoutStackContext = useSlideoutStackContextOptional();
  const topEntry = slideoutStackContext?.topEntry;
  const registerInlineHost = slideoutStackContext?.registerInlineHost;

  // The whole side-by-side treatment (docked pane, auto-open, entity-record
  // variant with its hidden in-panel chat) ships behind `builder_mode`, the
  // same gate as the rest of the builder surface area. Deliberately the
  // PERSISTED fast path, not failClosedUntilFresh (unlike `inline_proposals`
  // above): the claim below must be decidable on the FIRST render, or
  // navigating to a chat with a proposal on the stack paints the global
  // overlay + backdrop for the flag-fetch window and then swaps to the
  // docked pane — a visible flash. The stale-cache worst case here (a
  // just-gated org briefly docking the pane before the fresh flag flips it
  // back to the overlay) is rarer and milder than flashing the overlay for
  // every enabled org on every navigation. Flag off ⇒ no inline-host claim ⇒
  // every proposal click falls through to the global overlay, exactly the
  // pre-feature flow.
  const isBuilderModeGateEnabled = useFeatureFlag('builder_mode');
  const hasRoomForDockedProposal = useCanHostInlinePanel(
    MIN_CHAT_WIDTH_FOR_DOCKED_PROPOSAL
  );
  const canHostInlineProposal =
    isBuilderModeGateEnabled &&
    !isBuilderConversationView &&
    hasRoomForDockedProposal;

  // Layout effect, not a plain effect: it runs after commit but BEFORE the
  // browser paints, so when this page mounts with a proposal already on the
  // stack (deep link, navigation from a page showing the overlay), the claim
  // is registered before the global SlideoutRenderer ever gets a painted
  // frame — no overlay/backdrop flash on the way to the docked pane.
  useLayoutEffect(() => {
    if (!canHostInlineProposal || !registerInlineHost) {
      return;
    }
    return registerInlineHost(['proposal']);
  }, [canHostInlineProposal, registerInlineHost]);

  const dockedProposalEntry =
    canHostInlineProposal && topEntry?.kind === 'proposal' ? topEntry : null;

  // The docked pane and the reasoning/dynamic-view sidebar compete for the
  // same right-hand space; docking a proposal closes them (external stack
  // state driving the close — the ActionDetailPage toggleListSidebar
  // precedent).
  useEffect(() => {
    if (dockedProposalEntry) {
      handleCloseSidebar();
    }
  }, [dockedProposalEntry, handleCloseSidebar]);

  const {
    onResizeStart: handleProposalPaneResizeStart,
    resizeBy: resizeProposalPaneBy,
  } = useProposalPaneResize(
    setSlideoutWidth,
    setSlideoutIsResizing,
    slideoutWidth
  );

  useAutoOpenLatestProposal(
    canHostInlineProposal,
    latestProposalId,
    topEntry,
    openProposal
  );

  // Names what the mobile panel contains ("View automation"). Falls back to a
  // neutral noun rather than "undefined" while the subject resolves.
  const builderRecordLabel = builderSubjectEntityTypeName
    ? getTypeLabelByName(builderSubjectEntityTypeName).toLowerCase()
    : 'record';

  // Counted the same way the record surface counts it for the SaveBar —
  // proposed properties on the subject's overlay. Keeping one definition means
  // the review bar and the save toolbar it summons can never disagree about
  // how much is pending.
  const builderPendingChangeCount = useMemo(
    () => Object.keys(builderDraft?.overlay.properties ?? {}).length,
    [builderDraft?.overlay.properties]
  );

  // MOBILE ONLY. The compact composer (ConversationInput variant='simple') is
  // the same one the proposal side panel and email composer use, so phones get
  // one consistent input rather than a hero box squeezed into a narrow screen.
  // Desktop keeps the hero at every width, builder included.
  const useCompactComposer = !isLargeScreen;

  // Mobile only: the record panel opens FULL SCREEN over the chat. Closed by
  // default so the conversation is what the user lands in — the panel is a
  // review surface you go to, not the thing you start in.
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const openMobilePanel = useCallback(() => setIsMobilePanelOpen(true), []);
  const closeMobilePanel = useCallback(() => setIsMobilePanelOpen(false), []);

  // Leaving the mobile width with the overlay open would otherwise strand that
  // state: the desktop layout renders the panel inline and never reads it, so
  // returning to mobile would pop the overlay open unbidden.
  useEffect(() => {
    if (isLargeScreen) {
      setIsMobilePanelOpen(false);
    }
  }, [isLargeScreen]);

  // Static animation styles — injected once, use CSS custom properties for dynamic values.
  // Previously this was re-injected on every sidebarWidth change, creating a new <style>
  // element ~60 times/sec during resize which caused severe lag.
  React.useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-reasoning-chat', '');
    style.textContent = `
      @keyframes chatSatisfyingPush {
        0% { width: 100%; }
        55% { width: calc(100% - var(--sidebar-target-width) - 2%); }
        100% { width: calc(100% - var(--sidebar-target-width)); }
      }

      @keyframes sidebarSmoothSlide {
        0% { width: 0; opacity: 0; }
        55% { width: calc(var(--sidebar-target-width) + 2%); opacity: 0.9; }
        100% { width: var(--sidebar-target-width); opacity: 1; }
      }

      /* Global resize feedback */
      .is-resizing-sidebar * {
        pointer-events: none !important;
      }

      .is-resizing-sidebar {
        cursor: col-resize !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const {
    hasNewContent,
    scrollToBottom,
    handleScroll,
    handleNewMessagesClick,
    notifyNewContentIfScrolledUp,
  } = useChatAutoScroll({
    containerRef: chatContainerRef,
    conversationId,
    chains,
    partialSteps,
    currentConversation,
    currentTaskId,
  });

  // Redirect to conversations home when no conversation is selected
  useEffect(() => {
    if (!currentConversation && !conversationId && orgId) {
      navigate(`/${orgId}/conversations`);
    }
  }, [currentConversation, conversationId, orgId, navigate]);

  const loadConversation = useCallback(
    async (id: number | string) => {
      if (!orgId) {
        return;
      }

      // Claim the latest generation. Any load still in flight when this runs is
      // now stale and must not mutate UI state once it resolves.
      const loadGeneration = (loadGenerationRef.current += 1);

      setError(null);
      setPollingFailed(false);

      // True when this call refreshes the conversation already on screen
      // (e.g. the automatic reload after a reasoning task completes), as
      // opposed to an initial load, a switch to a different conversation,
      // or a reload triggered while no conversation is visible.
      const isRefreshOfRenderedConversation =
        renderedConversationIdRef.current === String(id);

      let failureDetail = '';
      for (
        let attempt = 1;
        attempt <= LOAD_CONVERSATION_MAX_ATTEMPTS;
        attempt++
      ) {
        try {
          const response = await fetchWithDefaults(
            `/api/orgs/${orgId}/reasoning/conversations/${id}/`
          );

          if (response.ok) {
            const data = await response.json();

            // A rapid conversation switch (A → B) can leave loadConversation(A)
            // resolving after the route already moved to B (this component stays
            // mounted; only the param changes, and there is no fetch abort).
            // Two guards, both required:
            //   1. Generation: a newer load (including the A → B → A case, where
            //      the id check below passes because the stale id matches the
            //      route again) has superseded this one — bail.
            //   2. Route mismatch: Effect 1 updates previousConversationIdRef
            //      synchronously on every switch, so it is the current route's
            //      conversation; a response for a different conversation must not
            //      render A over B or point useChatProgress at A's task id.
            if (
              loadGenerationRef.current !== loadGeneration ||
              (previousConversationIdRef.current !== null &&
                previousConversationIdRef.current !==
                  String(data.conversation.id))
            ) {
              return;
            }

            // Record this as the newest successful load so a still-in-flight
            // OLDER load that later fails cannot clobber this rendered content
            // with a stale error (see the failure guard below).
            lastSucceededLoadGenerationRef.current = loadGeneration;

            setCurrentConversation(data.conversation);
            setChains(data.chains || []);

            // A completed run may have EDITED a proposal in place
            // (update_proposed_action): same id, new payload, and no
            // websocket event covers that path — so an open detail surface
            // (the docked pane, the builder panel) keeps rendering the
            // pre-edit payload for up to the query's staleTime. On a refresh
            // of the on-screen conversation, mark every listed proposal's
            // detail stale; the default refetchType ('active') means only a
            // MOUNTED detail refetches — one request per open proposal,
            // none for closed ones, nothing on initial loads.
            if (isRefreshOfRenderedConversation) {
              const listedIds = new Set<number>(
                (data.chains || []).flatMap((chain: Chain) =>
                  (chain.steps || []).flatMap(step =>
                    (step.created_proposals || []).map(p => p.id)
                  )
                )
              );
              for (const listedId of listedIds) {
                void queryClient.invalidateQueries({
                  queryKey: proposalQueryKeys.detail(
                    parseInt(orgId, 10),
                    listedId
                  ),
                });
              }
            }

            // Resume polling if returning to a conversation with active
            // processing (page refresh, or navigating back mid-run). Only when
            // NOT already tracking a task (ref, to avoid a stale closure). The
            // tracker key is the conversation id, matching the visible/hidden
            // registration so toast suppression holds while on screen.
            if (!currentTaskIdRef.current) {
              const resumedConversationId = String(data.conversation.id);
              resumeActiveRun({
                orgId,
                conversationId: resumedConversationId,
                chains: data.chains || [],
                onResume: taskId => {
                  setCurrentTaskId(taskId);
                  setPollingConversationId(resumedConversationId);
                  setProgressStatus('Resuming…');
                },
              });
            }

            if (data.chains && data.chains.length > 0) {
              // Collapse all chains by default when new responses arrive
              const allChainIds = data.chains.map((chain: Chain) => chain.id);
              setCollapsedChains(new Set(allChainIds));

              // Auto-collapse all completed non-final steps
              const completedStepIds = data.chains
                .flatMap((chain: Chain) => chain.steps)
                .filter(
                  (step: ReasoningStep) =>
                    step.status === 'completed' && !step.is_final
                )
                .map((step: ReasoningStep) => step.id);
              setCollapsedSteps(new Set(completedStepIds));
            }
            return;
          }

          failureDetail = `HTTP ${response.status}`;
          if (response.status < 500 && response.status !== 429) {
            break;
          }
        } catch (err) {
          failureDetail =
            err instanceof Error ? `${err.name}: ${err.message}` : String(err);
        }

        if (attempt < LOAD_CONVERSATION_MAX_ATTEMPTS) {
          await wait(LOAD_CONVERSATION_RETRY_DELAY_MS);
        }
      }

      // Two independent reasons to suppress this failure:
      //   1. A strictly-NEWER load already SUCCEEDED. Overlapping refreshes of
      //      the SAME conversation share a route, so the route check below can't
      //      catch this: if load N+1 rendered fresh content and then this older
      //      load N fails, surfacing its error toast/banner would wrongly report
      //      a failure over content the user is actually looking at. Gate on the
      //      last SUCCEEDED generation (not merely the latest STARTED one): when
      //      overlapping loads all fail — e.g. a poll-driven refresh loop that
      //      keeps retriggering while each retry is still failing — none of them
      //      succeeded, so the failure the user must see is still surfaced.
      //   2. Route mismatch: the route moved away (A → B) while this load for A
      //      was in flight (component stays mounted; only the param changes; no
      //      fetch abort). Keyed on the requested id since there is no response
      //      body to read the conversation id from. A genuine same-conversation
      //      refresh (route still A) still surfaces the toast.
      if (
        lastSucceededLoadGenerationRef.current > loadGeneration ||
        (previousConversationIdRef.current !== null &&
          previousConversationIdRef.current !== String(id))
      ) {
        return;
      }

      console.error(`Failed to load conversation ${id}: ${failureDetail}`);
      if (isRefreshOfRenderedConversation) {
        // The user is still looking at valid content — a blocking banner
        // would wrongly suggest the conversation is gone. The next send,
        // poll cycle, or navigation reloads it anyway.
        toast.error("Couldn't refresh the conversation", {
          id: 'conversation-refresh-failed',
        });
      } else {
        setError("We couldn't load this conversation.");
      }
    },
    [orgId, queryClient]
  );

  // Handle chain regeneration (refresh/retry)
  const handleRetry = useCallback(
    async (chainId: string) => {
      if (!orgId || !conversationId || regeneratingChainId) {
        return;
      }

      setRegeneratingChainId(chainId);
      setError(null);

      try {
        const result = await conversationService.regenerateChain(
          orgId,
          conversationId,
          chainId
        );

        // Register with the tracker so completion/failure toasts survive the
        // page unmounting mid-regeneration. Keyed by the conversation the retry
        // was sent for (the route param at click time), matching the
        // visible/hidden registration key.
        if (result.task_id) {
          trackChatRun({ orgId, conversationId, taskId: result.task_id });
        }
        // The user may have switched conversation while the request was in
        // flight; Effect 1 has already reset task state for the new one, so
        // adopting this task would poll the wrong conversation with it.
        if (previousConversationIdRef.current !== conversationId) {
          return;
        }
        setCurrentTaskId(result.task_id);
        setProgressStatus('Retrying…');

        // Reload conversation to get the updated chain status
        await loadConversation(conversationId);
      } catch (err) {
        console.error('Failed to regenerate chain:', err);
        const errorMessage = (err instanceof Error && err.message) || '';

        // Map error messages to user-friendly text
        if (errorMessage.includes('only regenerate the last')) {
          setError('Can only retry the most recent response');
        } else if (errorMessage.includes('still processing')) {
          setError('Please wait for the current response to complete');
        } else if (errorMessage.includes('404')) {
          setError('Response not found');
        } else if (errorMessage.includes('403')) {
          setError("You don't have access to this conversation");
        } else {
          setError(
            'Something went wrong. Try again, and get in touch if it keeps happening.'
          );
        }
      } finally {
        setRegeneratingChainId(null);
      }
    },
    [orgId, conversationId, regeneratingChainId, loadConversation]
  );

  // Effect 1: Load conversation when ID changes (stable dependencies)
  useEffect(() => {
    loadConversations();

    // Switching directly between conversations (e.g. from one still-running
    // conversation to another via the sidebar/history) keeps this component
    // mounted and only changes the route param. Without clearing it, the
    // outgoing conversation's task lingers in currentTaskId/currentTaskIdRef,
    // which (a) makes the adopt guards in loadConversation and Effect 3 skip
    // the destination's active run — so it is never registered with the
    // tracker — and (b) points useChatProgress at the new conversation with the
    // old task id. Reset the task-tracking state on every genuine switch so the
    // destination's run is adopted fresh. Follow-ups reuse the same
    // conversationId and never re-run this effect; the nav-state taskId path
    // (Effect 2) runs after this effect and re-sets the task for that flow.
    const isConversationSwitch =
      previousConversationIdRef.current !== (conversationId ?? null);
    if (isConversationSwitch) {
      setCurrentTaskId(null);
      currentTaskIdRef.current = null;
      setPollingConversationId(null);
      setProgressStatus('');
      setPartialSteps([]);
    }
    previousConversationIdRef.current = conversationId ?? null;

    if (conversationId) {
      loadConversation(conversationId);
    } else {
      setCurrentConversation(null);
    }

    // Drop the reasoning sidebar: its chain belongs to the conversation we are
    // leaving. The dynamic view needs nothing here — it follows `?view=`, so
    // the URL we just arrived at already says whether one should be open, and
    // closing it by hand would discard a deep link the user had followed.
    closeReasoningSidebar();
    setActiveTab('conversation');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: load conversations once on initial render
  }, [conversationId, loadConversation]);

  // Re-read the conversation after the builder panel resolves a draft, by
  // Save OR Cancel.
  //
  // Save: on the create path the record's id reaches the client only on the
  // conversation row, written server-side at approval. Approving also resolves
  // the proposal, which drops the draft — so without this re-read the page
  // holds the `builder_entity_id: null` it loaded with, the panel has neither a
  // draft nor an id, and it renders its empty state over a record that exists.
  // Reloading the page used to be the only way out.
  //
  // Cancel: rejecting resolves the proposal too, but chat-built proposals get
  // no websocket update, so each card's `resolution` (from `created_proposals`)
  // is only as fresh as the last conversation read. Without this the rejected
  // card keeps reading "pending" until the next reload.
  const reloadConversationAfterResolution = useCallback(() => {
    if (conversationId) {
      loadConversation(conversationId);
    }
  }, [conversationId, loadConversation]);

  // Effect 2: Handle navigation state for polling (separate to avoid re-triggering)
  useEffect(() => {
    const state = location.state as {
      taskId?: string;
      fromAskAhoy?: boolean;
      fromConversationsHome?: boolean;
      userMessage?: string;
      initialPrompt?: string;
    } | null;

    // Handle taskId from navigation (new conversation started)
    if (
      state?.taskId &&
      (state?.fromAskAhoy || state?.fromConversationsHome) &&
      state.taskId !== processedTaskIdRef.current
    ) {
      // Mark this taskId as processed to avoid re-processing
      processedTaskIdRef.current = state.taskId;

      // Start polling for the task
      setCurrentTaskId(state.taskId);
      setPollingConversationId(conversationId || null);
      setProgressStatus('Starting reasoning…');

      // Set optimistic message from navigation state for instant UI feedback
      if (state.userMessage) {
        setOptimisticMessage(state.userMessage);
      }

      // Clear the navigation state. Keep `location.search`: navigating to the
      // bare pathname drops every search param, including `?slideoutEntityId=`
      // — so arriving here with a record open would silently close it.
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }

    // Handle initialPrompt from navigation (pre-fill input)
    if (state?.initialPrompt && !conversationId) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [
    location.state,
    conversationId,
    navigate,
    location.pathname,
    location.search,
  ]);

  // Effect 3: Fallback to ensure polling starts if chains have an active task
  // This catches any race conditions or timing issues with initial load
  useEffect(() => {
    // Only adopt from `chains` once they belong to the conversation in the URL.
    // On a switch A -> B, Effect 1 nulls currentTaskId synchronously but the
    // outgoing conversation's chains linger until loadConversation(B) resolves
    // and replaces both `currentConversation` and `chains` together. Without
    // this gate, Effect 3 would re-adopt A's active task against B's id, poll B
    // with A's stale task id, and leave B's real run unregistered.
    const chainsMatchConversation =
      String(currentConversation?.id ?? '') === String(conversationId ?? '');
    if (!chainsMatchConversation) {
      return;
    }
    if (!currentTaskId && chains.length > 0 && orgId && conversationId) {
      resumeActiveRun({
        orgId,
        conversationId,
        chains,
        onResume: taskId => {
          setCurrentTaskId(taskId);
          setPollingConversationId(conversationId);
          setProgressStatus('Resuming…');
        },
      });
    }
  }, [chains, currentTaskId, conversationId, orgId, currentConversation]);

  // Effect 4: Clear optimistic message when chains load (real data replaces optimistic UI)
  useEffect(() => {
    if (chains.length > 0) {
      setOptimisticMessage(null);
    }
  }, [chains.length]);

  // Effect 5: Reload conversation when proposal sidebar completes AI chat
  // The proposalUpdateCounter increments when ProposalDetailPanel.onComplete fires
  // This ensures the conversation chains update after AI processes proposal edits
  useEffect(() => {
    // Skip initial mount (counter starts at 0)
    if (proposalUpdateCounter === 0) {
      return;
    }

    if (conversationId) {
      loadConversation(conversationId);
    }
  }, [proposalUpdateCounter, conversationId, openProposalId, loadConversation]);

  // WS step.progress: replace-or-append the one step, through the same
  // snapshot comparison the polling path uses (see partialSteps.ts).
  const onWsStepProgress = useCallback(
    (event: StepProgressEvent) => {
      const incomingStep: ReasoningStepData = {
        id: event.step.id,
        step_number: event.step.step_number,
        stage: event.step.stage,
        status: event.step.status,
        content: event.step.content,
        is_final: event.step.is_final,
        tool_results: event.step.tool_results,
      };
      setPartialSteps(prev => mergeStepProgress(prev, incomingStep));
      notifyNewContentIfScrolledUp();
    },
    [notifyNewContentIfScrolledUp]
  );

  const stopTrackingRun = useCallback(() => {
    setCurrentTaskId(null);
    setPollingConversationId(null);
  }, []);

  const clearRunProgress = useCallback(() => {
    setProgressStatus('');
    setPartialSteps([]);
    setLoading(false);
  }, []);

  // The task a terminal handler is currently finishing, while its reload is
  // in flight. Both transports can report the same terminal state — polling
  // keeps ticking during the reload, and a WS `chain.completed` can land
  // beside a poll — so a second report for that task is dropped instead of
  // reloading again. Released when the reload settles, so a task re-adopted
  // afterwards (e.g. the reload failed and Effect 3 resumed it) can finish.
  const finishingTaskIdRef = useRef<string | null>(null);

  // A run reached a terminal state (by WS or by polling): reload the
  // conversation (or surface the failure), THEN stop tracking the task and
  // clear the progress UI. Tracking is kept through the reload so the thinking
  // indicator stays up until the answer renders, and so Effect 3 does not
  // re-adopt the run from the still-stale chains mid-reload.
  const finishRun = useCallback(
    async (failureMessage: string | null) => {
      const taskId = currentTaskIdRef.current;
      if (taskId !== null && finishingTaskIdRef.current === taskId) {
        return;
      }
      finishingTaskIdRef.current = taskId;
      try {
        if (failureMessage !== null) {
          setError(failureMessage);
        } else if (conversationId) {
          await loadConversation(conversationId);
        }
      } finally {
        // Release only our own guard. A run that settles after the user has
        // switched away must not release the guard a newer run set meanwhile:
        // the newer run's next poll would start a second reload, which makes
        // the first stale, and each settling reload would release the next one
        // — reloading forever and never rendering the answer.
        if (finishingTaskIdRef.current === taskId) {
          finishingTaskIdRef.current = null;
        }
        // If the user switched conversations while the reload was in flight and
        // the destination adopted its own run, currentTaskId now belongs to
        // THAT run — tearing down here would stop the new conversation from
        // polling and leave it stuck. Only clear when we're still tracking the
        // run that just finished.
        if (currentTaskIdRef.current === taskId) {
          stopTrackingRun();
          clearRunProgress();
        }
      }
    },
    [stopTrackingRun, clearRunProgress, conversationId, loadConversation]
  );

  const onWsChainCompleted = useCallback(
    async (_event: ChainCompletedEvent) => {
      await finishRun(null);
    },
    [finishRun]
  );

  const onWsChainFailed = useCallback(
    (event: ChainFailedEvent) => {
      void finishRun(`Reasoning failed: ${event.error || 'Unknown error'}`);
    },
    [finishRun]
  );

  const onWsTitleUpdated = useCallback((event: TitleUpdatedEvent) => {
    setCurrentConversation(prev =>
      prev ? { ...prev, title: event.title } : prev
    );
  }, []);

  // Memoize polling callbacks with stable dependencies to prevent polling restarts
  const onPollingUpdate = useCallback(
    async (
      data: ChatProgressData & {
        success?: boolean;
        progress?: { description?: string };
        description?: string;
        partial_steps?: ReasoningStepData[];
      }
    ) => {
      // The shared classifier also recognises the usePolling 404 fallback
      // (`{ status: 'completed' }`, sent once the task is gone) and a
      // terminal `chain_status`. Checking only `data.complete` left the chat
      // showing "Thinking…" forever when the WS completion was missed.
      // Paused is NOT terminal here: a main-chat run resumes after input.
      const outcome = classifyChatProgress(data);
      if (outcome !== 'in_progress') {
        // A celery-progress payload reports a failed task as
        // `complete: true, success: false`; the 404 fallback has no
        // `success`, and the reload reconciles against the real chain state.
        const failed = outcome === 'failed' || data.success === false;
        await finishRun(
          failed
            ? `Reasoning failed: ${data.result?.error || 'Unknown error'}`
            : null
        );
        return;
      }

      // celery-progress puts description in a nested 'progress' object.
      // setProgressStatus already no-ops when the string is unchanged.
      setProgressStatus(
        data.progress?.description || data.description || 'Thinking…'
      );

      // partial_steps is at root level from our backend enhancement
      const steps = data.partial_steps;
      if (steps && steps.length > 0) {
        // Polls fire every 500ms but steps change far less often; the
        // reconciler returns `prev` when nothing changed so React bails out.
        setPartialSteps(prev => reconcilePolledSteps(prev, steps));

        // The partialSteps effect surfaces the "New messages" button for a
        // scrolled-up user, but it only re-runs when the steps reference
        // changes — and the bail-out above intentionally keeps that
        // reference stable. So flag new content directly here on every poll
        // that carries steps; otherwise a scrolled-up user would never see
        // the button while a chain is mid-stream. (No-ops once already true.)
        notifyNewContentIfScrolledUp();
      }
    },
    [finishRun, notifyNewContentIfScrolledUp]
  );

  const onPollingError = useCallback(
    (error: string) => {
      console.error('Error polling progress:', error);
      stopTrackingRun();
      clearRunProgress();
      setError("We couldn't get progress updates.");
      setPollingFailed(true);
    },
    [stopTrackingRun, clearRunProgress]
  );

  // Set up real-time progress: WebSocket-first delivery with degraded polling
  // fallback. The hook owns transport coordination — when WS is connected,
  // polling degrades to a slow safety-net interval; when WS disconnects, it
  // restores to 500ms. Polling is gated on currentTaskId (same as before) but the WS
  // connects whenever conversationId is set so it's ready to receive
  // step.progress events the moment reasoning starts.
  const { wsStatus, isPolling } = useChatProgress({
    orgId,
    conversationId: conversationId || pollingConversationId,
    taskId: currentTaskId,
    onStepProgress: onWsStepProgress,
    onChainCompleted: onWsChainCompleted,
    onChainFailed: onWsChainFailed,
    onTitleUpdated: onWsTitleUpdated,
    onPollingUpdate,
    onPollingError,
    enabled: !!conversationId || !!pollingConversationId,
  });

  // Route health: polling is "ready" when either a task is actively being
  // polled (isPolling === true) or no task exists that requires polling.
  // Gating on isPolling alone would never fire for conversations without an
  // active task; gating on conversation-data alone reported the route healthy
  // before the polling transport had actually been evaluated. `pollingFailed`
  // is a dedicated signal (set only in onPollingError) so unrelated UI errors
  // — regenerate/follow-up failures, load errors from other requests — do not
  // suppress the marker and produce false timeout beacons.
  useViewHealthMarker('polling-ready', {
    ready:
      currentConversation !== null &&
      String(currentConversation.id) === String(conversationId) &&
      (isPolling ||
        wsStatus === 'connected' ||
        (!currentTaskId && !pollingFailed)),
  });

  const continueConversation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!followUpMessage.trim() || !currentConversation || !orgId) {
      return;
    }

    // Capture inputs so we can restore them if the send fails.
    const submittedMessage = followUpMessage;
    const submittedFiles = uploadedFiles;
    const submittedFileIds = submittedFiles.map(f => f.id);
    const sentConversationId = String(currentConversation.id);
    // The route can move to another conversation while the POST is in flight
    // (this component stays mounted; only the param changes). Effect 1 has
    // then already reset task and composer state for the new conversation.
    const isStillOnSentConversation = () =>
      previousConversationIdRef.current === sentConversationId;

    setLoading(true);
    setError(null);
    // Instant feedback: clear the composer and show the processing card NOW,
    // before the POST round-trip, so there's no dead window where nothing
    // appears to happen. Rolled back below if the send fails.
    setFollowUpMessage('');
    setUploadedFiles([]);
    setProgressStatus('Thinking…');

    // Repopulates the composer with the failed send's text/files. Guarded at
    // each call site below (not here) by whether the user is still viewing
    // the conversation this send belongs to — restoring into the wrong
    // conversation would overwrite the destination conversation's draft.
    const restoreComposer = () => {
      setFollowUpMessage(submittedMessage);
      setUploadedFiles(submittedFiles);
    };

    try {
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/${currentConversation.id}/messages/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: submittedMessage,
            file_ids: submittedFileIds,
            // The proposal the user most plausibly means by "the proposal":
            // the one open in the panel (docked pane or overlay), else the
            // thread's most recent OPEN proposal; builder conversations send
            // the proposal driving their panel (selection, else most recent).
            // The backend stores it per-chain and re-fetches at grounding
            // time, so a stale pick self-heals server-side. Absent means "no
            // open proposals in this thread".
            proposal_id: isBuilderConversationView
              ? (selectedProposalId ?? mostRecentBuilderProposalId)
              : (openProposalId ?? latestOpenProposalId),
            // In builder mode the PINNED subject rides along with every
            // message, so the user never has to reference the thing they are
            // visibly building. Gated on the subject, NOT on the panel being
            // rendered: collapsing the panel changes what is on screen, not
            // what is being built, and the agent must keep its subject either
            // way. Sent per-message rather than pinned on the conversation, so
            // each turn is stored with the record it was about and a later turn
            // cannot rewrite an earlier one. One record, replacing rather than
            // accumulating.
            // No builder attachment here. A builder conversation records its
            // own subject on the conversation row and the backend grounds
            // every chain from it, so the client no longer re-sends the record
            // with each message and cannot forget to.
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        const taskId = resolveTaskId(data);
        // Track under the conversation the message was SENT to — the same
        // key as that conversation's visible/hidden registration — so its
        // completion toast is suppressed while it is on screen, and still
        // fires if the user has already navigated away.
        if (taskId) {
          trackChatRun({ orgId, conversationId: sentConversationId, taskId });
        }
        if (!isStillOnSentConversation()) {
          return;
        }
        setCurrentTaskId(taskId);
        // A prior chain may still be emitting step.progress events for this
        // conversation's WS connection (scoped to conversationId, not
        // taskId). Clear its steps now so they can't render mixed in with
        // the new task's steps under the Thinking indicator.
        setPartialSteps([]);
        await loadConversation(sentConversationId);

        // Scroll to bottom after submitting human response
        setTimeout(() => {
          scrollToBottom();
        }, 200);
      } else if (isStillOnSentConversation()) {
        // Skip repopulating the composer, surfacing the error, and resetting
        // progress status if the user switched conversations while the
        // request was in flight: the composer now belongs to a different
        // conversation, and this error belongs to the one the user has left.
        // progressStatus is page-level (not conversation-scoped) and may
        // already reflect the destination conversation's own actively-polled
        // or WebSocket-driven run — clearing it unconditionally would corrupt
        // that conversation's UI.
        restoreComposer();
        setError(data.error || "We couldn't submit that response. Try again.");
        setProgressStatus('');
      }
    } catch (_error) {
      console.error('Failed to submit response:', _error);
      if (isStillOnSentConversation()) {
        restoreComposer();
        setError("We couldn't submit that response. Try again.");
        setProgressStatus('');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async () => {
    if (!orgId) {
      return;
    }

    try {
      // Include background agents if we're viewing an agent conversation
      const includeBackground = conversationId ? 'true' : 'false';
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/?include_background=${includeBackground}`
      );
      const data = await response.json();
      if (response.ok) {
        setConversations(data.conversations || []);
      }
    } catch {
      // Silently handle error - conversations list is not critical
    }
  };

  // This component requires a orgId - redirect if not available
  if (!orgId) {
    return <TeamRequiredState />;
  }
  const orgIdNumber = parseInt(orgId, 10);

  // Redirect to conversations home when no conversation is selected
  // The new ConversationsHome component is the primary landing page
  if (!currentConversation && !conversationId) {
    return null; // Show nothing while redirecting (useEffect above handles navigation)
  }

  const isSidePanelOpen = Boolean(sidebarDynamicViewId || sidebarChainId);
  const chatColumnWidth = resolveChatColumnWidth({
    // The docked panel rides its own 300ms pane-slide-in against the chat
    // column's width transition — the 750ms percent push is retired.
    isAnimating: false,
    isSidePanelOpen,
    sidePanelWidthPx: slideoutWidth,
    hasDockedPane: Boolean(dockedProposalEntry) || builderPanelVisible,
    dockedPaneWidthPx: slideoutWidth,
    isProposalOverlayVisible: proposalSidebarVisible,
    proposalOverlayWidthPx: proposalSidebarWidth,
  });

  return (
    <div className='h-full p-0 m-0 overflow-hidden flex flex-col'>
      {/* Mobile builder: the CHAT is the full-screen primary surface. The
          record panel is not stacked above it — it opens over the chat on
          demand (see the overlay below), because a permanently-docked chat
          left too little room for either surface to be usable. */}
      <div className='flex flex-1 h-full overflow-hidden p-0 m-0'>
        <ChatMainColumn
          width={chatColumnWidth}
          isSidePanelOpen={isSidePanelOpen}
          isResizing={proposalIsResizing || slideoutIsResizing}
        >
          {/* Builder mode gets its own header: the back link goes to the list
              of whatever is being built (automations, validation rules) rather
              than to Chat, and it is visible at every width — the generic one
              below hides its back button at `lg`, which stranded desktop
              builder users, where the builder is most used. */}
          {isBuilderConversationView ? (
            // Shown at EVERY width. On mobile the chat is the full-screen
            // primary surface and the record panel opens over it on demand, so
            // this header is the page header — and the only way out of the
            // builder.
            <BuilderChatHeader
              orgId={orgId}
              entityTypeName={builderSubjectEntityTypeName}
              entityId={builderSubjectId}
              lookupOrgId={builderOrgId}
              title={currentConversation?.title}
            />
          ) : (
            <ChatThreadHeader
              title={currentConversation?.title}
              activeTab={activeTab}
              onNavigateBack={handleNavigateBack}
              onToggleMaterialsTab={handleToggleMaterialsTab}
              onOpenShareModal={handleOpenShareModal}
            />
          )}

          {/* Content Area - Scrollable.
              No bottom padding: the composer is a flex sibling below this, not
              an overlay, so the thread already stops above it. The `pb-32` that
              used to be here was clearance sized for the hero box's wave
              footer and mb-14 overhang, and it read as a large dead gap under
              the last message. */}
          <div
            className='flex-1 min-w-0 overflow-y-auto overflow-x-hidden relative'
            ref={chatContainerRef}
            onScroll={handleScroll}
            tabIndex={-1}
            data-testid='chat-container'
            data-component='chat-container'
          >
            <TableScrollProvider scrollContainerRef={chatContainerRef}>
              {activeTab === 'conversation' ? (
                <ChatConversationThread
                  orgId={orgIdNumber}
                  chains={chains}
                  optimisticMessage={optimisticMessage}
                  isAwaitingFirstChain={
                    isPolling || Boolean(currentTaskId) || loading
                  }
                  isPolling={isPolling}
                  hasActiveTask={Boolean(currentTaskId)}
                  progressStatus={progressStatus}
                  partialSteps={partialSteps}
                  regeneratingChainId={regeneratingChainId}
                  chainFeedback={chainFeedback}
                  showInlineProposals={showInline}
                  isBuilderConversation={isBuilderConversationView}
                  shownProposalId={
                    isBuilderConversationView
                      ? builderDraft?.proposalId
                      : openProposalId
                  }
                  onSelectProposal={handleSelectProposal}
                  onOpenDynamicViewSidebar={handleOpenDynamicViewSidebar}
                  onOpenReasoningSidebar={handleOpenReasoningSidebar}
                  onRetry={handleRetry}
                  onFeedback={handleFeedback}
                />
              ) : (
                <div className='max-w-4xl mx-auto px-4'>
                  <MaterialsList
                    conversationId={
                      currentConversation?.id
                        ? parseInt(currentConversation.id)
                        : 0
                    }
                    orgId={orgId}
                    onOpenDynamicViewSidebar={handleOpenDynamicViewSidebar}
                    refetchKey={proposalUpdateCounter}
                  />
                </div>
              )}
            </TableScrollProvider>
          </div>

          {hasNewContent && (
            <NewMessagesButton onClick={handleNewMessagesClick} />
          )}

          {/* Pending-draft CTA. Mobile only — on desktop the panel is already
              on screen, so there is nothing to summon. Without this a draft
              can land while the user is mid-sentence and go unnoticed, because
              the panel is not visible. */}
          {isMobileBuilderView && (
            <BuilderReviewBar
              // The same number the slideout's SaveBar shows — both count the
              // draft's changed properties, so the bar can never promise a
              // different amount of work than the panel it opens. It was
              // `builderDraft ? 1 : 0`, a boolean wearing a count's clothes,
              // which read "1 change" over a proposal carrying several.
              pendingCount={builderPendingChangeCount}
              // Once a record exists there is always something worth opening,
              // draft or not — the edit path starts in that state.
              hasRecord={Boolean(builderSubjectId)}
              recordLabel={builderRecordLabel}
              onReview={openMobilePanel}
            />
          )}

          <FollowUpComposer
            isCompact={useCompactComposer}
            showDisclaimer={!isBuilderConversationView}
            value={followUpMessage}
            onChange={setFollowUpMessage}
            onSubmit={continueConversation}
            placeholder={followUpPlaceholder}
            loading={loading}
            error={error}
            orgId={orgIdNumber}
            uploadedFiles={uploadedFiles}
            onFilesChange={setUploadedFiles}
            uploadContextKey={conversationId}
          />
        </ChatMainColumn>

        {isSidePanelOpen && currentConversation && (
          <ChatSidePanel
            orgId={orgId}
            conversationId={parseInt(currentConversation.id)}
            isCreator={currentConversation.created_by === userProfile?.id}
            dynamicViewId={sidebarDynamicViewId}
            reasoningChain={
              chains.find(chain => chain.id === sidebarChainId) ?? null
            }
            showReasoning={Boolean(sidebarChainId)}
            width={slideoutWidth}
            isResizing={slideoutIsResizing}
            onResizeStart={handleProposalPaneResizeStart}
            onKeyboardResize={resizeProposalPaneBy}
            onClose={handleCloseSidebar}
            onOpenDynamicViewSidebar={handleOpenDynamicViewSidebar}
          />
        )}

        {/* Docked proposal pane — a real flex sibling, like the builder
            record panel below (and subject to the same "must be inside this
            flex row" constraint). Rendered only while this page holds the
            inline-host claim AND a proposal tops the slideout stack; every
            other case falls back to the global ProposalSidebar overlay. */}
        {dockedProposalEntry && (
          <InlineProposalPanel
            entry={dockedProposalEntry}
            width={slideoutWidth}
            onApproved={reloadConversationAfterResolution}
            onRejected={reloadConversationAfterResolution}
            resizeHandle={
              <EdgeResizeHandle
                edge='left'
                onResizeStart={handleProposalPaneResizeStart}
                onKeyboardResize={resizeProposalPaneBy}
                width={slideoutWidth}
                minWidth={INLINE_PANEL_MIN_WIDTH}
                maxWidth={INLINE_PANEL_MAX_WIDTH}
                isResizing={slideoutIsResizing}
                aria-label='Resize proposal panel. Use left and right arrow keys to resize.'
              />
            }
          />
        )}

        {/* The object being built. This MUST be inside the flex row above —
            the element wrapping that row is `display: block`, so a sibling
            placed after it stacks BELOW the full-height chat column, lands at
            y=viewportHeight, and is clipped away by `overflow-hidden`. The
            chat column has already given up `slideoutWidth` for this panel, so
            the symptom is an empty strip beside the chat and a panel nobody
            can see — present in the DOM, with the right content, entirely
            off-screen. Do not move this out of the flex row. */}
        <BuilderRecordPanelDesktop
          visible={builderPanelVisible}
          slideoutWidth={slideoutWidth}
          orgId={builderOrgId}
          entityTypeName={builderSubjectEntityTypeName}
          entityId={builderSubjectId}
          overlays={builderOverlays}
          draft={builderDraft}
          onApproved={reloadConversationAfterResolution}
          onRejected={reloadConversationAfterResolution}
        />
      </div>

      {/* Mobile builder: the record panel, full screen over the chat.
          Rendered as a sibling of the chat column rather than inside it so it
          covers the header too — the panel is its own surface while open, with
          its own way back (onClosePanel), not a section of the conversation. */}
      <BuilderRecordPanelMobileOverlay
        visible={isMobileBuilderView && isMobilePanelOpen}
        orgId={builderOrgId}
        entityTypeName={builderSubjectEntityTypeName}
        entityId={builderSubjectId}
        overlays={builderOverlays}
        draft={builderDraft}
        onApproved={reloadConversationAfterResolution}
        onRejected={reloadConversationAfterResolution}
        onClosePanel={closeMobilePanel}
      />

      <ConversationModals
        orgId={orgId}
        conversationId={currentConversation?.id}
        conversationTitle={currentConversation?.title}
        isShareModalOpen={isShareModalOpen}
        onCloseShareModal={handleCloseShareModal}
        feedbackModalChainId={feedbackModalChainId}
        feedbackModalOpen={feedbackModalOpen}
        feedbackModalSteps={feedbackModalSteps}
        onFeedbackModalClose={handleFeedbackModalClose}
        onFeedbackSubmit={handleStructuredFeedback}
      />
    </div>
  );
}
